import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getTasksLightweight, getTask, getTaskCounts } from '@/utils/api';
import { toast } from 'sonner';
import { Search, Plus, ChevronLeft, ChevronRight, Filter, Clock, AlertTriangle, Loader2, GripVertical, X, MessageSquare, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import TaskListItem from '@/components/TaskListItem';
import TaskSummaryView from '@/components/TaskSummaryView';
import ChatPanel from '@/components/ChatPanel';
import TaskDetailForm from '@/components/TaskDetailForm';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import { STATUS_LABELS } from '@/utils/constants';

const ThreePanelTaskPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDeadline, setFilterDeadline] = useState('');
  const [filterUnread, setFilterUnread] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [taskListCollapsed, setTaskListCollapsed] = useState(false);
  const [counts, setCounts] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  
  // Resizable chat panel state
  const [chatWidth, setChatWidth] = useState(380);
  const [isResizing, setIsResizing] = useState(false);
  
  // Mobile state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [showMobileTaskList, setShowMobileTaskList] = useState(true);

  // Check for mobile on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle chat panel resize
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing) return;
    const containerWidth = window.innerWidth;
    const newWidth = containerWidth - e.clientX;
    // Min 280px, max 600px
    setChatWidth(Math.min(Math.max(newWidth, 280), 600));
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterDeadline, filterUnread]);

  // Handle selected task from URL param (e.g., after creating a new task)
  useEffect(() => {
    const selectedId = searchParams.get('selected');
    if (selectedId && tasks.length > 0) {
      const taskToSelect = tasks.find(t => t.id === selectedId);
      if (taskToSelect) {
        handleTaskSelect(taskToSelect);
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('selected');
        setSearchParams(newParams);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, searchParams]);

  useEffect(() => {
    filterTasks();
  }, [tasks, searchQuery]);

  const fetchCounts = async () => {
    try {
      const response = await getTaskCounts();
      setCounts(response.data);
    } catch (error) {
      console.error('Failed to load counts');
    }
  };

  const fetchTasks = async () => {
    try {
      const params = {};
      if (filterStatus) params.task_status = filterStatus;
      if (filterDeadline) params.deadline = filterDeadline;
      if (filterUnread) params.has_unread = true;
      
      // Batch: fetch tasks and counts in parallel
      const [tasksRes, countsRes] = await Promise.all([
        getTasksLightweight(params),
        getTaskCounts()
      ]);
      
      const newTasks = tasksRes.data;
      setTasks(newTasks);
      setCounts(countsRes.data);
      
      // Auto-select first task only if nothing is currently selected
      if (newTasks.length > 0 && !selectedTask) {
        try {
          const detailRes = await getTask(newTasks[0].id);
          setSelectedTask(detailRes.data);
        } catch (err) {
          console.error('Failed to load task detail:', err);
        }
      }
    } catch (error) {
      if (error.response?.status !== 401) toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const filterTasks = () => {
    let filtered = [...tasks];

    if (searchQuery) {
      filtered = filtered.filter(
        (task) =>
          task.internal_cad_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          task.party_cad_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          task.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          task.product_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredTasks(filtered);
  };

  const handleTaskSelect = async (task) => {
    try {
      const response = await getTask(task.id);
      setSelectedTask(response.data);
      setShowCreateForm(false);
    } catch (error) {
      if (error.response?.status !== 401) toast.error('Failed to load task details');
    }
  };

  const handleTaskUpdate = () => {
    // Refresh the task list
    fetchTasks();
    setShowCreateForm(false);
    // Re-fetch the currently selected task to get latest data
    if (selectedTask?.id) {
      getTask(selectedTask.id)
        .then((response) => setSelectedTask(response.data))
        .catch(() => {});
    }
  };

  const handleCreateNew = () => {
    // Navigate to the full-page create form
    navigate('/tasks/new');
  };

  const canCreateTask = true; // All roles can create tasks

  const clearFilters = () => {
    setFilterStatus('');
    setFilterDeadline('');
    setFilterUnread(false);
    setSearchQuery('');
  };

  return (
    <CollapsibleLayout>
      <div className="h-[calc(100vh-48px)] flex bg-background relative">
        {/* Mobile Header Bar */}
        {isMobile && selectedTask && !showCreateForm && (
          <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-border p-2 flex items-center justify-between md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSelectedTask(null); setShowMobileTaskList(true); }}
            >
              <ChevronLeft size={18} />
              Back
            </Button>
            <span className="text-sm font-medium truncate flex-1 text-center">{selectedTask.internal_cad_no}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowMobileChat(!showMobileChat)}
              className="relative"
            >
              <MessageSquare size={18} />
            </Button>
          </div>
        )}

        {/* Filter Sidebar - Hidden on mobile */}
        {showFilters && !taskListCollapsed && !isMobile && (
          <div className="w-44 bg-white border-r border-border flex flex-col">
            <div className="p-3 border-b border-border">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Filters</h3>
            </div>
            
            {/* Deadline Filters */}
            <div className="p-3 border-b border-border/50">
              <h4 className="text-xs font-medium text-foreground mb-2">By Deadline</h4>
              <div className="space-y-1">
                {counts?.overdue > 0 && (
                  <button
                    onClick={() => setFilterDeadline(filterDeadline === 'overdue' ? '' : 'overdue')}
                    className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded-md transition-colors ${
                      filterDeadline === 'overdue' ? 'bg-red-50 text-red-700' : 'hover:bg-muted text-muted-foreground'
                    }`}
                  >
                    <span className="flex items-center gap-1.5">
                      <AlertTriangle size={12} className="text-red-500" />
                      Overdue
                    </span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${filterDeadline === 'overdue' ? 'bg-red-100' : 'bg-muted'}`}>
                      {counts?.overdue || 0}
                    </span>
                  </button>
                )}
                <button
                  onClick={() => setFilterDeadline(filterDeadline === 'today' ? '' : 'today')}
                  className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded-md transition-colors ${
                    filterDeadline === 'today' ? 'bg-orange-50 text-orange-700' : 'hover:bg-muted text-muted-foreground'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <Clock size={12} className="text-orange-500" />
                    Due Today
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-xs ${filterDeadline === 'today' ? 'bg-orange-100' : 'bg-muted'}`}>
                    {counts?.due_today || 0}
                  </span>
                </button>
                <button
                  onClick={() => setFilterDeadline(filterDeadline === 'this_week' ? '' : 'this_week')}
                  className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded-md transition-colors ${
                    filterDeadline === 'this_week' ? 'text-foreground font-medium' : 'hover:bg-muted text-muted-foreground'
                  }`}
                  style={filterDeadline === 'this_week' ? { backgroundColor: 'hsl(45, 90%, 50%)', color: 'hsl(220, 60%, 20%)' } : {}}
                >
                  <span>This Week</span>
                  <span className={`px-1.5 py-0.5 rounded text-xs ${filterDeadline === 'this_week' ? 'bg-white/30' : 'bg-muted'}`}>
                    {counts?.due_this_week || 0}
                  </span>
                </button>
              </div>
            </div>

            {/* Status Filters */}
            <div className="p-3 flex-1 overflow-y-auto">
              <h4 className="text-xs font-medium text-foreground mb-2">By Status</h4>
              <div className="space-y-1">
                {counts?.by_status && (() => {
                  const merged = {};
                  Object.entries(counts.by_status).forEach(([status, count]) => {
                    const label = STATUS_LABELS[status] || status;
                    if (!merged[label]) merged[label] = { statuses: [], count: 0 };
                    merged[label].statuses.push(status);
                    merged[label].count += count;
                  });
                  return Object.entries(merged).map(([label, { statuses, count }]) => (
                    <button
                      key={label}
                      onClick={() => setFilterStatus(statuses.includes(filterStatus) ? '' : statuses[0])}
                      className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded-md transition-colors ${
                        statuses.includes(filterStatus) ? 'text-white' : 'hover:bg-muted text-muted-foreground'
                      }`}
                      style={statuses.includes(filterStatus) ? { backgroundColor: 'hsl(220, 60%, 20%)' } : {}}
                    >
                      <span className="truncate">{label}</span>
                      <span className={`px-1.5 py-0.5 rounded text-xs ${statuses.includes(filterStatus) ? 'bg-white/20' : 'bg-muted'}`}>
                        {count}
                      </span>
                    </button>
                  ));
                })()}
              </div>
            </div>

            {/* Unread */}
            {counts?.unread > 0 && (
              <div className="p-3 border-t border-border/50">
                <button
                  onClick={() => setFilterUnread(!filterUnread)}
                  className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded-md transition-colors ${filterUnread ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                  data-testid="unread-filter-btn"
                >
                  <span>{filterUnread ? 'Showing Unread Only' : 'Unread Messages'}</span>
                  <span className={`px-1.5 py-0.5 rounded text-xs ${filterUnread ? 'bg-blue-500' : 'bg-blue-100'}`}>{counts?.unread}</span>
                </button>
              </div>
            )}

            {(filterStatus || filterDeadline || filterUnread) && (
              <div className="p-3 border-t border-border/50">
                <button
                  onClick={clearFilters}
                  className="w-full text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Task List - Full width on mobile when no task selected */}
        <div
          className={`bg-white border-r border-border flex flex-col transition-all duration-300 ${
            isMobile 
              ? (selectedTask && !showMobileTaskList ? 'hidden' : 'w-full') 
              : (taskListCollapsed ? 'w-12' : 'w-64')
          }`}
        >
          {taskListCollapsed ? (
            <div className="flex items-center justify-center p-3 border-b border-border">
              <button
                onClick={() => setTaskListCollapsed(false)}
                className="p-2 hover:bg-muted rounded-md"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          ) : (
            <>
              <div className="p-3 border-b border-border" style={{ backgroundColor: 'hsl(220, 60%, 20%)' }}>
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-sm font-semibold text-white">CAD Files</h2>
                  <div className="flex items-center gap-1">
                    <span 
                      className="px-1.5 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: 'hsl(45, 90%, 50%)', color: 'hsl(220, 60%, 20%)' }}
                    >
                      {filteredTasks.length}
                    </span>
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className={`p-1 rounded-md ${showFilters ? 'bg-white/20' : 'hover:bg-white/10'} text-white`}
                      title="Toggle filters"
                    >
                      <Filter size={14} />
                    </button>
                    <button
                      onClick={() => setTaskListCollapsed(true)}
                      className="p-1 hover:bg-white/10 rounded-md text-white"
                    >
                      <ChevronLeft size={14} />
                    </button>
                  </div>
                </div>

                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/50" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 h-8 text-xs bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  />
                </div>

                {canCreateTask && (
                  <Button
                    onClick={handleCreateNew}
                    className="w-full mt-2 h-8 text-xs font-medium"
                    style={{ backgroundColor: 'hsl(45, 90%, 50%)', color: 'hsl(220, 60%, 20%)' }}
                    data-testid="new-cad-btn"
                  >
                    <Plus size={14} className="mr-1" />
                    New CAD
                  </Button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="p-8 text-center text-muted-foreground text-xs flex flex-col items-center">
                    <Loader2 size={18} className="animate-spin mb-2" />
                    Loading...
                  </div>
                ) : filteredTasks.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground text-xs">
                    {searchQuery || filterStatus || filterDeadline ? 'No tasks match filters' : 'No tasks yet'}
                  </div>
                ) : (
                  <div>
                    {filteredTasks.map((task) => (
                      <TaskListItem
                        key={task.id}
                        task={task}
                        isSelected={selectedTask?.id === task.id}
                        onClick={() => {
                          handleTaskSelect(task);
                          if (isMobile) setShowMobileTaskList(false);
                        }}
                        currentUserId={user?.id}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Main Content - Hidden on mobile when task list is showing */}
        <div className={`flex-1 flex flex-col min-w-0 ${isMobile && showMobileTaskList ? 'hidden' : ''} ${isMobile && selectedTask ? 'pt-12' : ''}`}>
          {showCreateForm ? (
            <div className="h-full bg-white overflow-hidden">
              <TaskDetailForm
                task={null}
                onUpdate={handleTaskUpdate}
                onCancel={() => {
                  setShowCreateForm(false);
                  if (tasks.length > 0) {
                    setSelectedTask(tasks[0]);
                  }
                  if (isMobile) setShowMobileTaskList(true);
                }}
              />
            </div>
          ) : selectedTask ? (
            <TaskSummaryView task={selectedTask} onUpdate={handleTaskUpdate} />
          ) : (
            <div className="flex items-center justify-center h-full bg-white">
              <div className="text-center text-muted-foreground">
                <p className="text-base">No task selected</p>
                <p className="text-xs mt-1">Select a CAD file from the list</p>
              </div>
            </div>
          )}
        </div>

        {/* Resizable Chat Panel - Desktop only */}
        {selectedTask && !showCreateForm && !isMobile && (
          <>
            {/* Resize Handle */}
            <div
              onMouseDown={handleMouseDown}
              className={`w-1 cursor-ew-resize flex items-center justify-center hover:bg-amber-400 transition-colors ${isResizing ? 'bg-amber-400' : 'bg-border'}`}
              style={{ backgroundColor: isResizing ? 'hsl(45, 90%, 50%)' : undefined }}
              title="Drag to resize"
            >
              <GripVertical size={12} className="text-muted-foreground" />
            </div>
            <div style={{ width: chatWidth }} className="transition-none">
              <ChatPanel taskId={selectedTask.id} taskName={selectedTask.product_name || selectedTask.client_name} />
            </div>
          </>
        )}

        {/* Mobile Chat Drawer */}
        {isMobile && selectedTask && showMobileChat && (
          <div className="fixed inset-0 z-50 bg-black/50" onClick={() => setShowMobileChat(false)}>
            <div 
              className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-3 border-b" style={{ backgroundColor: 'hsl(220, 60%, 20%)' }}>
                <h3 className="text-white font-medium text-sm">Chat</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowMobileChat(false)} className="text-white hover:bg-white/10">
                  <X size={18} />
                </Button>
              </div>
              <div className="h-[calc(100vh-52px)]">
                <ChatPanel taskId={selectedTask.id} taskName={selectedTask.product_name || selectedTask.client_name} />
              </div>
            </div>
          </div>
        )}
      </div>
    </CollapsibleLayout>
  );
};

export default ThreePanelTaskPage;
